/** Helpers for the RRM Channels map — AP extraction, frequency math, overlap detection. */

export const DEFAULT_MIN_FREQ_GAP_MHZ = 20;
export const DEFAULT_NEIGHBOR_RSSI_FLOOR_DBM = -85;
/** Fixed map marker radius (m) — visual only, not used for overlap detection. */
export const MAP_MARKER_RADIUS_M = 24;
export const MAP_LABEL_OFFSET_M = 24;

/** Matches backend CollectChannelAPInfos / optimize channel step. */
export const CHANNEL_RADIO_SCOPE = {
  OPTIMIZER: 'optimizer',
  DIAGNOSTIC: 'diagnostic',
};

export function channelToFrequencyMHz(reportedBand, channel) {
  const ch = Number(channel);
  if (!Number.isFinite(ch) || ch <= 0) return 0;
  const band = String(reportedBand || '').toLowerCase().trim();
  switch (band) {
    case '2.4ghz':
    case '2.4':
    case '2ghz':
      return 2407 + 5 * ch;
    case '5ghz':
    case '5':
      return 5000 + 5 * ch;
    case '6ghz':
    case '6':
    case '7ghz':
    case '7':
    case '6/7ghz':
      return 5955 + 5 * (ch - 1);
    default:
      break;
  }
  if (ch >= 1 && ch <= 14) return 2407 + 5 * ch;
  if (ch >= 32 && ch <= 177) return 5000 + 5 * ch;
  if (ch >= 1 && ch <= 233) return 5950 + 5 * ch;
  return 0;
}

export function normalizeBand(reportedBand) {
  const band = String(reportedBand || '').toLowerCase().trim();
  if (band.includes('6') || band.includes('7')) return '6GHz';
  if (band.includes('5')) return '5GHz';
  if (band.includes('2.4') || band.includes('2ghz')) return '2.4GHz';
  return reportedBand || 'unknown';
}

export function isBackhaulBand(band) {
  const b = normalizeBand(band);
  return b === '5GHz' || b === '6GHz';
}

export function getDeviceType(capabilities) {
  if (!capabilities) return 'Unknown';
  const model = String(capabilities.model || '');
  const compatible = String(capabilities.compatible || '').toLowerCase();
  if (compatible === 'edgecore_eap102' || compatible.includes('edgecore')) return 'CAP';
  const lower = model.toLowerCase();
  if (lower.includes('nvidia') || lower.includes('jetson') || lower.includes('qrb5165')) return 'AI';
  if (lower.includes('cap')) return 'CAP';
  if (lower.includes('node')) return 'Node';
  return model || 'Unknown';
}

export function getDeviceCompatible(device) {
  const caps = device?.capabilities;
  if (caps && typeof caps === 'object' && caps.compatible) {
    return String(caps.compatible).toLowerCase();
  }
  if (device?.compatible) return String(device.compatible).toLowerCase();
  return '';
}

/** jnet2 mesh backhaul only — Juganu CAPs and Nodes. */
export function isJnet2CapOrNode(device) {
  if (getDeviceCompatible(device) !== 'jnet2') return false;
  const type = getDeviceType(device?.capabilities);
  return type === 'CAP' || type === 'Node';
}

function isStationMode(mode) {
  const m = String(mode || '').toLowerCase().trim();
  return m === 'station' || m === 'sta';
}

/** Match backend algo.IsAPMode — explicit mode:"ap" only. */
function isApInterface(iface) {
  if (!iface) return false;
  const mode = iface.Mode ?? iface.mode;
  return String(mode || '').toLowerCase().trim() === 'ap';
}

function isStationInterface(iface) {
  if (!iface) return false;
  return isStationMode(iface.Mode ?? iface.mode);
}

function buildRadioMap(radios) {
  const radioMap = {};
  (radios || []).forEach((radio) => {
    if (radio?.name) radioMap[String(radio.name).toLowerCase()] = radio;
  });
  return radioMap;
}

function resolveRadioForInterface(iface, idx, radios, radioMap) {
  const radioName = String(
    iface?.Radio?.Name || iface?.radio?.name || iface?.radio?.Ref || '',
  ).toLowerCase();
  if (radioName && radioMap[radioName]) return radioMap[radioName];

  if (Array.isArray(radios) && radios.length > 0) {
    const ifaceIdx =
      parseInt(String(iface?.Name || iface?.name || '').replace(/\D/g, ''), 10) || idx + 1;
    const byIndex = radios.find((r) => {
      const rIdx = parseInt(String(r?.name || '').replace(/\D/g, ''), 10);
      return rIdx === ifaceIdx;
    });
    if (byIndex) return byIndex;
    return radios[Math.min(idx, radios.length - 1)];
  }
  return null;
}

export function getDeviceGps(device) {
  const effective = getEffectiveGpsFromUtils(device);
  if (effective) {
    return { lat: effective.lat, lng: effective.lng };
  }
  return null;
}

function getEffectiveGpsFromUtils(device) {
  const override = device?.gps_override;
  if (override) {
    const rawLat = override.lat ?? override.latitude;
    const rawLng = override.lng ?? override.longitude;
    if (rawLat != null && rawLng != null) {
      const lat = typeof rawLat === 'number' ? rawLat : parseFloat(rawLat);
      const lng = typeof rawLng === 'number' ? rawLng : parseFloat(rawLng);
      if (Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)) {
        return { lat, lng };
      }
    }
  }

  const gps = device?.gps || device?.gps_location;
  if (!gps) return null;
  const rawLat = gps.lat ?? gps.latitude;
  const rawLng = gps.lng ?? gps.longitude;
  if (rawLat == null || rawLng == null) return null;
  const lat = typeof rawLat === 'number' ? rawLat : parseFloat(rawLat);
  const lng = typeof rawLng === 'number' ? rawLng : parseFloat(rawLng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
}

function resolveDeviceGps(device, devicesBySerial) {
  const direct = getDeviceGps(device);
  if (direct) return direct;

  const deviceType = getDeviceType(device?.capabilities);
  if (deviceType !== 'AI' || !device?.parent_serial) return null;

  const parent = devicesBySerial.get(String(device.parent_serial).toLowerCase());
  if (parent?.compatible !== 'jnet2') return null;
  return getDeviceGps(parent);
}


function collectPlannerApRadiosFromDevice(device) {
  const radios = device?.radios || [];
  const radioMap = buildRadioMap(radios);
  const interfaces = device.private_wifi?.interfaces || device.private_wifi?.Interfaces || [];
  const apEntries = [];

  interfaces.forEach((iface, idx) => {
    if (!isApInterface(iface)) return;
    const radio = resolveRadioForInterface(iface, idx, radios, radioMap);
    if (radio) apEntries.push({ radio, role: 'ap' });
  });

  return apEntries;
}

/** Primary mesh backhaul AP radio on a device (6 GHz preferred, then 5 GHz). */
export function getPrimaryBackhaulApRadio(device) {
  const candidates = collectPlannerApRadiosFromDevice(device)
    .map(({ radio }) => {
      const channel = Number(radio?.channel);
      const band = normalizeBand(radio?.band);
      if (!Number.isFinite(channel) || channel <= 0 || !isBackhaulBand(band)) {
        return null;
      }
      return { channel, band };
    })
    .filter(Boolean);

  if (candidates.length === 0) {
    return null;
  }

  const bandRank = (band) => (band === '6GHz' ? 2 : band === '5GHz' ? 1 : 0);
  candidates.sort((a, b) => bandRank(b.band) - bandRank(a.band) || b.channel - a.channel);
  return candidates[0];
}

/** STA uplink radios — diagnostic only (mesh co-channel with parent, not planner-actionable). */
function collectStaUplinkRadiosFromDevice(device) {
  const radios = device?.radios || [];
  const radioMap = buildRadioMap(radios);
  const interfaces = device.private_wifi?.interfaces || device.private_wifi?.Interfaces || [];
  const entries = [];

  interfaces.forEach((iface, idx) => {
    if (!isStationInterface(iface)) return;
    const radio = resolveRadioForInterface(iface, idx, radios, radioMap);
    if (radio) entries.push({ radio, role: 'sta_uplink' });
  });

  return entries;
}

function collectRadioEntriesForScope(device, scope) {
  if (scope === CHANNEL_RADIO_SCOPE.DIAGNOSTIC) {
    const seen = new Set();
    const out = [];
    [...collectPlannerApRadiosFromDevice(device), ...collectStaUplinkRadiosFromDevice(device)].forEach((entry) => {
      const radioName = String(entry.radio?.name || '').toLowerCase();
      const band = normalizeBand(entry.radio?.band);
      const key = `${radioName}|${band}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push(entry);
    });
    return out;
  }
  return collectPlannerApRadiosFromDevice(device);
}

function pushApRadio(markers, seen, device, gps, radio, isCap, role = 'ap') {
  const serial = device.serial_number;
  const channel = Number(radio.channel);
  if (!Number.isFinite(channel) || channel <= 0) return false;

  const band = normalizeBand(radio.band);
  if (!isBackhaulBand(band)) return false;

  const frequencyMHz = channelToFrequencyMHz(band, channel);
  if (!frequencyMHz) return false;

  const radioName = String(radio.name || '').toLowerCase();
  const key = `${serial.toLowerCase()}|${radioName}|${band}|${channel}`;
  if (seen.has(key)) return false;
  seen.add(key);

  markers.push({
    id: key,
    serial,
    serialSuffix: serial.slice(-4),
    hasGps: Boolean(gps),
    lat: gps?.lat ?? null,
    lng: gps?.lng ?? null,
    channel,
    band,
    radioName: radio.name || radioName,
    frequencyMHz: Math.round(frequencyMHz),
    channelWidth: Number(radio.channel_width) || null,
    isCap,
    role,
    plannerScope: role === 'ap',
  });
  return true;
}

/** Extract backhaul radios for channel analysis. Optimizer scope = connected, explicit AP mode. */
export function extractApRadiosFromDevices(devices, options = {}) {
  const scope = options.scope || CHANNEL_RADIO_SCOPE.OPTIMIZER;
  if (!Array.isArray(devices)) {
    return {
      apRadios: [],
      skippedNoApRadios: [],
      devicesWithGps: 0,
      mapPlottableCount: 0,
      scope,
    };
  }

  const devicesBySerial = new Map();
  devices.forEach((d) => {
    if (d?.serial_number) devicesBySerial.set(String(d.serial_number).toLowerCase(), d);
  });

  const apRadios = [];
  const skippedNoApRadios = [];
  const seen = new Set();
  let devicesWithGps = 0;
  let mapPlottableCount = 0;

  devices.forEach((device) => {
    const serial = device?.serial_number;
    if (!serial) return;

    if (!isJnet2CapOrNode(device)) return;

    if (scope === CHANNEL_RADIO_SCOPE.OPTIMIZER && device.connected === false) return;

    const radioEntries = collectRadioEntriesForScope(device, scope);
    if (radioEntries.length === 0) {
      skippedNoApRadios.push(serial);
      return;
    }

    const gps = resolveDeviceGps(device, devicesBySerial);
    if (gps) devicesWithGps += 1;
    const isCap = getDeviceType(device.capabilities) === 'CAP';
    radioEntries.forEach(({ radio, role }) => {
      if (pushApRadio(apRadios, seen, device, gps, radio, isCap, role) && gps && role === 'ap') {
        mapPlottableCount += 1;
      }
    });
  });

  return {
    apRadios,
    skippedNoApRadios: [...new Set(skippedNoApRadios)],
    devicesWithGps,
    mapPlottableCount,
    scope,
  };
}

/** Unique backhaul AP channels per band from private_wifi AP-mode radios (5/6 GHz). */
export function collectApChannelsByBandFromDevices(devices) {
  const byBand = { '5GHz': new Set(), '6GHz': new Set() };
  if (!Array.isArray(devices)) {
    return { '5GHz': [], '6GHz': [] };
  }

  devices.forEach((device) => {
    collectPlannerApRadiosFromDevice(device).forEach(({ radio }) => {
      const channel = Number(radio?.channel);
      if (!Number.isFinite(channel) || channel <= 0) return;
      const band = normalizeBand(radio?.band);
      if (band === '5GHz' || band === '6GHz') {
        byBand[band].add(channel);
      }
    });
  });

  return {
    '5GHz': [...byBand['5GHz']].sort((a, b) => a - b),
    '6GHz': [...byBand['6GHz']].sort((a, b) => a - b),
  };
}

/** AP radios with GPS coordinates — for map rendering only. */
export function filterMapPlottableApRadios(apRadios) {
  if (!Array.isArray(apRadios)) return [];
  return apRadios.filter((m) => m.hasGps && m.lat != null && m.lng != null);
}

export function haversineDistanceM(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Spectrum overlap when center frequencies are closer than the required gap. */
export function frequenciesOverlapMhz(freqA, freqB, minGapMHz) {
  if (!freqA || !freqB) return false;
  return Math.abs(freqA - freqB) < minGapMHz;
}

export function canonicalSerial(serial) {
  return String(serial || '').trim().replace(/:/g, '').toLowerCase();
}

function neighborInt(raw, key) {
  const v = raw?.[key] ?? raw?.[key.toLowerCase()];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function neighborBandLabel(raw) {
  const bandRaw = raw?.Band ?? raw?.band;
  if (typeof bandRaw === 'string' && bandRaw.trim()) {
    const band = bandRaw.toLowerCase().trim();
    if (band.includes('2.4') || band === '2' || band.includes('2ghz')) return '2.4GHz';
    if (band.includes('5')) return '5GHz';
    if (band.includes('6') || band.includes('7')) return '6GHz';
  }
  const n = neighborInt(raw, 'Band');
  if (n === 1 || n === 24) return '2.4GHz';
  if (n === 2 || n === 5) return '5GHz';
  if (n === 3 || n === 6 || n === 7) return '6GHz';
  return '';
}

function computeRssiDownDbm(staSnr, staNf) {
  if (staNf == null || staNf === 0) return 0;
  let v = Number(staSnr) + Number(staNf);
  if (!Number.isFinite(v)) return 0;
  if (v > 0) v = -v;
  return v;
}

/** Parse one neighbour scan row into the shape used by BuildNeighborEvidenceIndex. */
export function parseNeighborObservation(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const apNodeId = raw.AP_NodeID || raw.ap_node_id || '';
  const neighborSerial = canonicalSerial(apNodeId);
  const band = neighborBandLabel(raw);
  const channel = neighborInt(raw, 'Channel');
  if (!neighborSerial || !band || channel <= 0) return null;
  const staSnr = neighborInt(raw, 'STA_SNR');
  const staNf = neighborInt(raw, 'STA_NF');
  return {
    neighborSerial,
    band,
    channel,
    rssiDownDbm: computeRssiDownDbm(staSnr, staNf),
  };
}

/** Convert bulk or single /tree-mgmt/neighbors API payloads into a serial→observations map. */
export function parseNeighborReportsFromApi(response) {
  const out = {};
  if (!response) return out;

  const ingestReport = (report) => {
    const serial = canonicalSerial(report?.Serial || report?.serial);
    if (!serial) return;
    const observations = (report.Neighbors || report.neighbors || [])
      .map((raw) => parseNeighborObservation(raw))
      .filter(Boolean);
    out[serial] = observations;
  };

  if (Array.isArray(response)) {
    response.forEach(ingestReport);
    return out;
  }
  if (response.Serial || response.serial) {
    ingestReport(response);
  }
  return out;
}

/**
 * O(1) heard-pair lookups — mirrors algo.BuildNeighborEvidenceIndex.
 * `neighbors` is serial → parsed observations from golden scan reports.
 */
export function buildNeighborEvidenceIndex(neighbors, rssiFloorDbm = DEFAULT_NEIGHBOR_RSSI_FLOOR_DBM) {
  const floor = Number.isFinite(Number(rssiFloorDbm)) ? Number(rssiFloorDbm) : DEFAULT_NEIGHBOR_RSSI_FLOOR_DBM;
  const pairs = new Map();
  const scanned = new Set();

  const pairKey = (a, b) => (a <= b ? `${a}|${b}` : `${b}|${a}`);
  const lexFirst = (a, b) => (a <= b ? a : b);

  Object.entries(neighbors || {}).forEach(([serial, observations]) => {
    const canonicalA = canonicalSerial(serial);
    if (!canonicalA) return;
    scanned.add(canonicalA);
    (observations || []).forEach((ob) => {
      const canonicalB = canonicalSerial(ob.neighborSerial);
      if (!canonicalB || canonicalB === canonicalA) return;
      const heard = ob.rssiDownDbm !== 0 && ob.rssiDownDbm >= floor;
      const key = pairKey(canonicalA, canonicalB);
      const entry = pairs.get(key) || {
        bestRssiDbm: 0,
        aHeardB: false,
        bHeardA: false,
        canonicalA: lexFirst(canonicalA, canonicalB),
      };
      if (canonicalA === entry.canonicalA) {
        if (heard) entry.aHeardB = true;
      } else if (heard) {
        entry.bHeardA = true;
      }
      if (heard && (entry.bestRssiDbm === 0 || ob.rssiDownDbm > entry.bestRssiDbm)) {
        entry.bestRssiDbm = ob.rssiDownDbm;
      }
      pairs.set(key, entry);
    });
  });

  return {
    rssiFloorDbm: floor,
    scanned,
    lookup(serialA, serialB) {
      const a = canonicalSerial(serialA);
      const b = canonicalSerial(serialB);
      if (!a || !b || a === b) {
        return { heard: false, bestRssiDbm: 0, heardAToB: false, heardBToA: false };
      }
      const entry = pairs.get(pairKey(a, b));
      if (!entry) {
        return { heard: false, bestRssiDbm: 0, heardAToB: false, heardBToA: false };
      }
      if (a === entry.canonicalA) {
        return {
          heard: entry.aHeardB || entry.bHeardA,
          bestRssiDbm: entry.bestRssiDbm,
          heardAToB: entry.aHeardB,
          heardBToA: entry.bHeardA,
        };
      }
      return {
        heard: entry.aHeardB || entry.bHeardA,
        bestRssiDbm: entry.bestRssiDbm,
        heardAToB: entry.bHeardA,
        heardBToA: entry.aHeardB,
      };
    },
  };
}

/** Same rules as the channel planner heard edges (no GPS radius). */
export function apPairHeard(serialA, serialB, neighborEvidenceIndex) {
  const a = canonicalSerial(serialA);
  const b = canonicalSerial(serialB);
  if (a === b) {
    return { heard: true, evidence: 'same_chassis', bestRssiDbm: null };
  }
  if (!neighborEvidenceIndex) {
    return { heard: false, evidence: null, bestRssiDbm: null };
  }
  const ev = neighborEvidenceIndex.lookup(a, b);
  if (ev.heard) {
    return { heard: true, evidence: 'heard', bestRssiDbm: ev.bestRssiDbm };
  }
  return { heard: false, evidence: null, bestRssiDbm: null };
}

export function findChannelOverlaps(markers, minFreqGapMHz, neighborEvidenceIndex, options = {}) {
  const plannerOnly = options.plannerOnly !== false;
  const overlaps = [];
  const conflictIds = new Set();

  for (let i = 0; i < markers.length; i += 1) {
    for (let j = i + 1; j < markers.length; j += 1) {
      const a = markers[i];
      const b = markers[j];
      if (a.band !== b.band) continue;

      const plannerScope = a.plannerScope !== false && b.plannerScope !== false
        && a.role !== 'sta_uplink' && b.role !== 'sta_uplink';
      if (plannerOnly && !plannerScope) continue;

      const heard = apPairHeard(a.serial, b.serial, neighborEvidenceIndex);
      if (!heard.heard) continue;

      const freqSepMHz = Math.abs(a.frequencyMHz - b.frequencyMHz);
      if (!frequenciesOverlapMhz(a.frequencyMHz, b.frequencyMHz, minFreqGapMHz)) continue;

      const distanceM = (a.hasGps && b.hasGps && a.lat != null && b.lat != null)
        ? haversineDistanceM(a.lat, a.lng, b.lat, b.lng)
        : null;
      if (plannerScope) {
        conflictIds.add(a.id);
        conflictIds.add(b.id);
      }
      overlaps.push({
        id: `${a.id}::${b.id}`,
        distanceM,
        freqSepMHz,
        minGapMHz: minFreqGapMHz,
        neighborEvidence: heard.evidence,
        bestRssiDbm: heard.bestRssiDbm,
        plannerScope,
        a,
        b,
      });
    }
  }

  overlaps.sort((x, y) => {
    if (x.freqSepMHz !== y.freqSepMHz) return x.freqSepMHz - y.freqSepMHz;
    const rssiA = x.bestRssiDbm ?? -999;
    const rssiB = y.bestRssiDbm ?? -999;
    return rssiB - rssiA;
  });
  return { overlaps, conflictIds };
}

/** Stable hue per channel within a band for map coloring. */
export function getChannelColor(channel, band, conflict = false) {
  if (conflict) return '#d32f2f';
  const baseHue = band === '6GHz' ? 280 : 210;
  const hue = (baseHue + Number(channel) * 13) % 360;
  return `hsl(${hue}, 72%, 42%)`;
}

/** Offset label position for markers sharing the same GPS so callouts do not stack. */
export function attachLabelPositions(markers, labelOffsetM = MAP_LABEL_OFFSET_M) {
  if (!markers.length) return [];

  const offsetM = Math.max(2, labelOffsetM);
  const groups = new Map();
  markers.forEach((marker) => {
    const key = `${marker.lat.toFixed(6)}|${marker.lng.toFixed(6)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(marker);
  });
  groups.forEach((group) => group.sort((a, b) => a.id.localeCompare(b.id)));

  return markers.map((marker) => {
    const key = `${marker.lat.toFixed(6)}|${marker.lng.toFixed(6)}`;
    const group = groups.get(key) || [marker];
    if (group.length <= 1) {
      return { ...marker, labelLat: marker.lat, labelLng: marker.lng };
    }
    const idx = group.findIndex((m) => m.id === marker.id);
    const angleRad = (2 * Math.PI * idx) / group.length;
    const labelPos = offsetLatLng(marker.lat, marker.lng, offsetM, angleRad);
    return { ...marker, labelLat: labelPos.lat, labelLng: labelPos.lng };
  });
}

export function offsetLatLng(lat, lng, distanceM, angleRad) {
  const latRad = (lat * Math.PI) / 180;
  const metersPerDegLat = 111320;
  const metersPerDegLng = 111320 * Math.cos(latRad);
  const dLat = (distanceM / metersPerDegLat) * Math.cos(angleRad);
  const dLng = metersPerDegLng > 0 ? (distanceM / metersPerDegLng) * Math.sin(angleRad) : 0;
  return { lat: lat + dLat, lng: lng + dLng };
}
export function filterMarkersByBand(markers, bandFilter) {
  if (!bandFilter || bandFilter === 'all') return markers;
  return markers.filter((m) => m.band === bandFilter);
}

/** Keep only AP markers involved in at least one overlap pair. */
export function filterOverlappingMarkers(markers, conflictIds) {
  if (!conflictIds || conflictIds.size === 0) return [];
  return markers.filter((m) => conflictIds.has(m.id));
}

export function describeEmptyChannelMap({
  apRadios,
  devices,
  skippedNoApRadios,
  mapPlottableCount = 0,
  mapMarkerCount = 0,
  showOverlappingOnly = true,
}) {
  if (mapMarkerCount > 0) return null;
  if (mapPlottableCount === 0 && Array.isArray(apRadios) && apRadios.length > 0) {
    return 'No GPS coordinates — map view is empty. Heard-pair conflict analysis below still includes all AP radios.';
  }
  if (showOverlappingOnly && mapPlottableCount > 0) {
    return 'No conflicting APs with GPS to plot. Switch to “All APs” on the map, or review heard-pair conflicts below.';
  }
  if (!Array.isArray(devices) || devices.length === 0) {
    return 'Devices are still loading or none are connected for this project.';
  }
  if (skippedNoApRadios.length > 0 && (!apRadios || apRadios.length === 0)) {
    return 'jnet2 devices found but no plottable 5/6 GHz AP radios (check private WiFi AP interfaces and radio channel config).';
  }
  return 'No jnet2 CAP/Node AP radios found.';
}
